import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { mastersAPI } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

/* ── Simple CRUD Modal ── */
const ItemModal = ({ title, item, fields, onClose, onSaved, saveFn }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const cardBg = darkMode ? '#1E293B' : '#fff';
  const border = darkMode ? '#334155' : '#E2E8F0';
  const inp = { background: darkMode ? '#0F172A' : '#F8FAFC', color: darkMode ? '#F1F5F9' : '#0F172A', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, padding: '10px 12px', fontSize: 14, width: '100%', outline: 'none', boxSizing: 'border-box' };
  const lbl = { color: darkMode ? '#94A3B8' : '#64748B', fontSize: 11, fontWeight: 700, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.04em' };

  const [form, setForm] = useState(() => {
    const init = {};
    fields.forEach(f => { init[f.key] = item ? (item[f.key] || '') : (f.default || ''); });
    return init;
  });
  const [saving, setSaving] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    setSaving(true);
    try { await saveFn(form); onSaved(); } catch (err) { alert(err.response?.data?.detail || 'Save failed'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: cardBg, borderRadius: 16, width: '100%', maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', boxShadow: '0 24px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ height: 4, background: `linear-gradient(90deg, ${acc}, ${acc}88)` }} />
        <form onSubmit={handleSubmit} style={{ padding: 24 }}>
          <h3 style={{ color: darkMode ? '#F1F5F9' : '#0F172A', fontSize: 18, fontWeight: 800, margin: '0 0 20px' }}>{title}</h3>
          <div style={{ display: 'grid', gap: 14 }}>
            {fields.map(f => (
              <div key={f.key}>
                <label style={lbl}>{f.label}{f.required && <span style={{ color: '#ef4444', marginLeft: 2 }}>*</span>}</label>
                {f.type === 'select' ? (
                  <select style={inp} value={form[f.key]} onChange={set(f.key)} required={f.required}>
                    {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input style={inp} type={f.type || 'text'} required={f.required} value={form[f.key]} onChange={set(f.key)} placeholder={f.placeholder || ''} />
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
            <button type="button" onClick={onClose} style={{ padding: '10px 20px', background: 'transparent', border: `1px solid ${border}`, borderRadius: 8, fontWeight: 600, cursor: 'pointer', color: darkMode ? '#94A3B8' : '#64748B' }}>Cancel</button>
            <button type="submit" disabled={saving} style={{ padding: '10px 24px', background: saving ? '#94A3B8' : acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? '⏳ Saving…' : '💾 Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Masters Table Section ── */
const MasterSection = ({ title, icon, items, loading, columns, onAdd, onEdit, onDelete }) => {
  const { darkMode, color } = useTheme();
  const acc = color.swatch;
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };
  const border = darkMode ? '#334155' : '#E2E8F0';
  const card = { background: darkMode ? '#1E293B' : '#fff', border: `1px solid ${border}`, borderRadius: 12 };

  return (
    <div style={{ marginBottom: 28 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <h2 style={{ ...pg, fontSize: 16, fontWeight: 800, margin: 0 }}>{icon} {title}</h2>
        <button onClick={onAdd} style={{ padding: '8px 16px', background: acc, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', fontSize: 13 }}>➕ Add</button>
      </div>
      <div style={{ ...card, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 30, textAlign: 'center', ...muted }}>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center' }}>
            <div style={{ ...pg, fontWeight: 600 }}>No {title.toLowerCase()} yet</div>
            <div style={{ ...muted, fontSize: 13 }}>Click "Add" to create one</div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: darkMode ? '#0F172A' : '#F8FAFC' }}>
                {columns.map(c => (
                  <th key={c.key} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase' }}>{c.label}</th>
                ))}
                <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: darkMode ? '#64748B' : '#94A3B8', textTransform: 'uppercase' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.id} style={{ borderTop: `1px solid ${border}` }}>
                  {columns.map(c => (
                    <td key={c.key} style={{ padding: '10px 14px', fontSize: 13, ...pg }}>{it[c.key] || '—'}</td>
                  ))}
                  <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                    <button onClick={() => onEdit(it)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, marginRight: 6 }} title="Edit">✏️</button>
                    <button onClick={() => onDelete(it.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }} title="Delete">🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

/* ── Main Masters Page ── */
export const MastersContent = () => {
  const { darkMode, color } = useTheme(); // eslint-disable-line no-unused-vars
  const pg = { color: darkMode ? '#F1F5F9' : '#0F172A' };
  const muted = { color: darkMode ? '#64748B' : '#94A3B8' };

  const [units, setUnits] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(true);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingSubcategories, setLoadingSubcategories] = useState(true);

  // Modal state
  const [modal, setModal] = useState(null); // { type, item }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type, id }

  const fetchUnits = useCallback(async () => {
    setLoadingUnits(true);
    try { const r = await mastersAPI.listUnits(); setUnits(r.data.units || r.data || []); } catch { setUnits([]); }
    setLoadingUnits(false);
  }, []);

  const fetchBrands = useCallback(async () => {
    setLoadingBrands(true);
    try { const r = await mastersAPI.listBrands(); setBrands(r.data.brands || r.data || []); } catch { setBrands([]); }
    setLoadingBrands(false);
  }, []);

  const fetchCategories = useCallback(async () => {
    setLoadingCategories(true);
    try { const r = await mastersAPI.listCategories(); setCategories(r.data || []); } catch { setCategories([]); }
    setLoadingCategories(false);
  }, []);

  const fetchSubcategories = useCallback(async () => {
    setLoadingSubcategories(true);
    try { const r = await mastersAPI.listSubcategories(); setSubcategories(r.data || []); } catch { setSubcategories([]); }
    setLoadingSubcategories(false);
  }, []);

  useEffect(() => { fetchUnits(); fetchBrands(); fetchCategories(); fetchSubcategories(); }, [fetchUnits, fetchBrands, fetchCategories, fetchSubcategories]);

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      if (deleteConfirm.type === 'unit') await mastersAPI.deleteUnit(deleteConfirm.id);
      else if (deleteConfirm.type === 'brand') await mastersAPI.deleteBrand(deleteConfirm.id);
      else if (deleteConfirm.type === 'category') await mastersAPI.deleteCategory(deleteConfirm.id);
      else if (deleteConfirm.type === 'subcategory') await mastersAPI.deleteSubcategory(deleteConfirm.id);
      const t = deleteConfirm.type;
      setDeleteConfirm(null);
      if (t === 'unit') fetchUnits();
      else if (t === 'brand') fetchBrands();
      else if (t === 'category') { fetchCategories(); fetchSubcategories(); }
      else if (t === 'subcategory') fetchSubcategories();
    } catch { alert('Delete failed'); }
  };

  const unitFields = [
    { key: 'unit_name', label: 'Unit Name', required: true, placeholder: 'e.g. Pcs, Kg, Ltr' },
  ];
  const brandFields = [
    { key: 'brand_name', label: 'Brand Name', required: true, placeholder: 'e.g. Samsung, Apple' },
  ];
  const categoryFields = [
    { key: 'category_name', label: 'Category Name', required: true, placeholder: 'e.g. Electronics, Clothing' },
  ];
  const subcategoryFields = [
    { key: 'category_name', label: 'Parent Category', required: true, type: 'select', options: ['', ...categories.map(c => c.category_name)] },
    { key: 'subcategory_name', label: 'Subcategory Name', required: true, placeholder: 'e.g. Mobile Phones, Shirts' },
  ];

  const unitColumns = [{ key: 'unit_name', label: 'Unit Name' }];
  const brandColumns = [{ key: 'brand_name', label: 'Brand Name' }];
  const categoryColumns = [{ key: 'category_name', label: 'Category Name' }];
  const subcategoryColumns = [{ key: 'category_name', label: 'Parent Category' }, { key: 'subcategory_name', label: 'Subcategory Name' }];

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <div style={{ marginBottom: 24 }}>
          <h1 style={{ ...pg, fontSize: 22, fontWeight: 800, margin: 0 }}>📂 Masters</h1>
          <p style={{ ...muted, fontSize: 13, margin: '4px 0 0' }}>Manage units, brands, categories and subcategories</p>
        </div>

        <MasterSection
          title="Units" icon="📏" items={units} loading={loadingUnits}
          columns={unitColumns}
          onAdd={() => setModal({ type: 'unit', item: null })}
          onEdit={item => setModal({ type: 'unit', item })}
          onDelete={id => setDeleteConfirm({ type: 'unit', id })}
        />

        <MasterSection
          title="Brands" icon="🏷️" items={brands} loading={loadingBrands}
          columns={brandColumns}
          onAdd={() => setModal({ type: 'brand', item: null })}
          onEdit={item => setModal({ type: 'brand', item })}
          onDelete={id => setDeleteConfirm({ type: 'brand', id })}
        />

        <MasterSection
          title="Categories" icon="📁" items={categories} loading={loadingCategories}
          columns={categoryColumns}
          onAdd={() => setModal({ type: 'category', item: null })}
          onEdit={item => setModal({ type: 'category', item })}
          onDelete={id => setDeleteConfirm({ type: 'category', id })}
        />

        <MasterSection
          title="Subcategories" icon="📂" items={subcategories} loading={loadingSubcategories}
          columns={subcategoryColumns}
          onAdd={() => setModal({ type: 'subcategory', item: null })}
          onEdit={item => setModal({ type: 'subcategory', item })}
          onDelete={id => setDeleteConfirm({ type: 'subcategory', id })}
        />

      {/* Item Modal */}
      {modal && (
        <ItemModal
          title={
            modal.item
              ? `Edit ${modal.type === 'unit' ? 'Unit' : modal.type === 'brand' ? 'Brand' : modal.type === 'category' ? 'Category' : 'Subcategory'}`
              : `New ${modal.type === 'unit' ? 'Unit' : modal.type === 'brand' ? 'Brand' : modal.type === 'category' ? 'Category' : 'Subcategory'}`
          }
          item={modal.item}
          fields={
            modal.type === 'unit' ? unitFields
            : modal.type === 'brand' ? brandFields
            : modal.type === 'category' ? categoryFields
            : subcategoryFields
          }
          onClose={() => setModal(null)}
          onSaved={() => {
            setModal(null);
            if (modal.type === 'unit') fetchUnits();
            else if (modal.type === 'brand') fetchBrands();
            else if (modal.type === 'category') { fetchCategories(); fetchSubcategories(); }
            else fetchSubcategories();
          }}
          saveFn={async data => {
            if (modal.type === 'unit') {
              modal.item ? await mastersAPI.updateUnit(modal.item.id, data) : await mastersAPI.createUnit(data);
            } else if (modal.type === 'brand') {
              modal.item ? await mastersAPI.updateBrand(modal.item.id, data) : await mastersAPI.createBrand(data);
            } else if (modal.type === 'category') {
              modal.item ? await mastersAPI.updateCategory(modal.item.id, data) : await mastersAPI.createCategory(data);
            } else {
              modal.item ? await mastersAPI.updateSubcategory(modal.item.id, data) : await mastersAPI.createSubcategory(data);
            }
          }}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: darkMode ? '#1E293B' : '#fff', borderRadius: 14, padding: 24, maxWidth: 'min(1120px, calc(100vw - 12px))', maxHeight: '98dvh', overflowY: 'auto', width: '100%', textAlign: 'center' }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>⚠️</div>
            <h3 style={{ ...pg, marginBottom: 8 }}>Delete {deleteConfirm.type}?</h3>
            <p style={{ ...muted, fontSize: 13, marginBottom: 20 }}>This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDeleteConfirm(null)} style={{ padding: '9px 20px', background: 'transparent', border: `1px solid ${darkMode ? '#475569' : '#CBD5E1'}`, borderRadius: 8, cursor: 'pointer', ...pg }}>Cancel</button>
              <button onClick={handleDelete} style={{ padding: '9px 20px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Masters = () => (
  <Layout><div style={{ padding: 'clamp(12px, 3vw, 28px)' }}><MastersContent /></div></Layout>
);

export default Masters;
